import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Globe, 
  Clock, 
  Settings, 
  Users, 
  Calendar,
  Loader2, 
  AlertCircle,
  CheckCircle,
  XCircle,
  FileText,
  Hash,
} from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';

interface Company {
  id: string;
  name: string;
  legalName: string;
  siret?: string;
  vatNumber?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  phone: string;
  email: string;
  website?: string;
  timezone: string;
  defaultSlotSizeMinutes: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ShopsResponse {
  success: boolean;
  data: {
    shops: Array<{
      id: string;
      name: string;
      isActive: boolean;
    }>;
    total: number;
  };
}

export function CompanyViewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [company, setCompany] = useState<Company | null>(null);
  const [shops, setShops] = useState<Array<{id: string; name: string; isActive: boolean}>>([]);
  const [loading, setLoading] = useState(true);
  const [loadingShops, setLoadingShops] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadCompany();
      loadCompanyShops();
    }
  }, [id]);

  const loadCompany = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/companies/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch company');
      }

      const data = await response.json();
      
      if (data.success) {
        setCompany(data.data.company);
      } else {
        throw new Error(data.message || 'Failed to load company');
      }
    } catch (error) {
      console.error('Company fetch error:', error);
      setError('Failed to load company');
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyShops = async () => {
    try {
      setLoadingShops(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/admin/shops?companyId=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: ShopsResponse = await response.json();
        
        if (data.success) {
          setShops(data.data.shops);
        }
      }
      // If shops endpoint doesn't support companyId filter yet, just continue without shops
    } catch (error) {
      console.error('Shops fetch error:', error);
      // Don't set error state for shops, it's optional
    } finally {
      setLoadingShops(false);
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    if (isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-3 h-3 mr-1" />
          Active
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircle className="w-3 h-3 mr-1" />
          Inactive
        </span>
      );
    }
  };

  // Removed unused formatAddress function

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center space-x-2">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          <span className="text-gray-600">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">{error || 'Company not found'}</span>
          </div>
          <button
            onClick={loadCompany}
            className="mt-2 text-sm text-red-600 hover:text-red-700 underline"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/companies')}
          className="inline-flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t('common.back')}</span>
        </button>
        
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-display font-bold text-textPrimary mb-2 flex items-center space-x-2">
              <span>{company.name}</span>
              <HelpIcon 
                docPath="03-entreprises#03-4-consultation" 
                tooltip="Documentation : Consultation d'une entreprise"
              />
            </h1>
            <p className="text-textSecondary">
              {company.legalName}
            </p>
            <div className="mt-2">
              {getStatusBadge(company.isActive)}
            </div>
          </div>
          <button
            onClick={() => navigate(`/companies/${company.id}/edit`)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <Edit className="w-4 h-4" />
            <span>{t('companyForm.edit')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Building2 className="w-5 h-5 mr-2 text-primary-600" />
              {t('companyForm.basicInfo')}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyForm.name')}
                </label>
                <p className="text-sm text-gray-900">{company.name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyForm.legalName')}
                </label>
                <p className="text-sm text-gray-900">{company.legalName}</p>
              </div>

              {company.siret && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companyForm.siret')}
                  </label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <Hash className="w-3 h-3 mr-1 text-gray-400" />
                    {company.siret}
                  </p>
                </div>
              )}

              {company.vatNumber && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('companyForm.vatNumber')}
                  </label>
                  <p className="text-sm text-gray-900 flex items-center">
                    <Hash className="w-3 h-3 mr-1 text-gray-400" />
                    {company.vatNumber}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-primary-600" />
              {t('companyForm.address')}
            </h2>
            
            <div className="space-y-2">
              <p className="text-sm text-gray-900">
                {company.address.street}
              </p>
              <p className="text-sm text-gray-900">
                {company.address.city} {company.address.postalCode}
              </p>
              <p className="text-sm text-gray-900">
                {company.address.country}
              </p>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Phone className="w-5 h-5 mr-2 text-primary-600" />
              {t('companyForm.contact')}
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <Phone className="w-4 h-4 mr-3 text-gray-400" />
                <span className="text-sm text-gray-900">{company.phone}</span>
              </div>

              <div className="flex items-center">
                <Mail className="w-4 h-4 mr-3 text-gray-400" />
                <span className="text-sm text-gray-900">{company.email}</span>
              </div>

              {company.website && (
                <div className="flex items-center">
                  <Globe className="w-4 h-4 mr-3 text-gray-400" />
                  <a 
                    href={company.website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {company.website}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Associated Shops */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Users className="w-5 h-5 mr-2 text-primary-600" />
              {t('companyView.associatedShops')}
            </h2>
            
            {loadingShops ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                <span className="text-sm text-gray-500">{t('common.loading')}</span>
              </div>
            ) : shops.length === 0 ? (
              <div className="text-center py-4">
                <Users className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">
                  {t('companyView.noShops')}
                </p>
                <button
                  onClick={() => navigate('/shops/create')}
                  className="mt-2 text-sm text-primary-600 hover:text-primary-700 underline"
                >
                  {t('companyView.createFirstShop')}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {shops.map(shop => (
                  <div key={shop.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">{shop.name}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusBadge(shop.isActive)}
                      <button
                        onClick={() => navigate(`/shops/${shop.id}`)}
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {t('companyView.viewShop')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Settings */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-primary-600" />
              {t('companyForm.settings')}
            </h2>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyForm.timezone')}
                </label>
                <p className="text-sm text-gray-900 flex items-center">
                  <Clock className="w-3 h-3 mr-1 text-gray-400" />
                  {company.timezone}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyForm.defaultSlotSize')}
                </label>
                <p className="text-sm text-gray-900 flex items-center">
                  <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                  {company.defaultSlotSizeMinutes} minutes
                </p>
              </div>
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-primary-600" />
              {t('companyView.metadata')}
            </h2>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyView.createdAt')}
                </label>
                <p className="text-sm text-gray-900">{formatDate(company.createdAt)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyView.updatedAt')}
                </label>
                <p className="text-sm text-gray-900">{formatDate(company.updatedAt)}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('companyView.companyId')}
                </label>
                <p className="text-sm text-gray-500 font-mono">{company.id}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}



